<template>
  <v-card class="mb-6">
    <!--v-card-title>
      <span class="text-h5 mb-7">Générer un article</span>
    </v-card-title-->
    <v-card-text>
      <!-- Site selection moved to main page - this component now receives siteId as prop -->

      <v-row>
        <v-col cols="12">
          <div class="d-flex align-center flex-wrap">
            <v-text-field
              v-model="topic"
              label="Topic / angle"
              :counter="topicCharacterLimit"
              :counter-value="countTopicCharacters"
              :error="topicError || topicTooLong"
              :error-messages="topicValidationMessages"
              :hint="topicLengthHint"
              persistent-hint
              outlined
              dense
              :class="[
                'flex-grow-1 mr-4 topic-field',
                {
                  'topic-field--near-limit': topicNearLimit,
                  'topic-field--over-limit': topicTooLong,
                },
              ]"
            ></v-text-field>
            <v-checkbox
              v-model="topicIsTitle"
              label="Titre définitif"
              hide-details
              class="ml-1 pt-0 topic-is-title-checkbox"
              dense
            ></v-checkbox>
          </div>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <div class="d-flex align-center mb-2">
            <span class="text-subtitle-1 mr-4">Sources</span>
            <v-btn color="primary" small @click="addSourceField">
              <v-icon left small>{{ icons.mdiPlus }}</v-icon>
              <span>source</span>
            </v-btn>
            <v-btn
              class="ml-2"
              color="primary"
              small
              :loading="isSourceAutoLoading"
              @click="addSourceAutoField"
            >
              <v-icon left small>{{ icons.mdiPlus }}</v-icon>
              <span>sources auto</span>
            </v-btn>
            <v-btn
              class="ml-2"
              color="primary"
              small
              @click="multiSourcesDialog = true"
            >
              <v-icon left small>{{ icons.mdiPlus }}</v-icon>
              <span>Multi sources</span>
            </v-btn>
          </div>

          <v-row
            v-for="(source, index) in sources"
            :key="'source-' + index"
            class="mb-4"
          >
            <v-col cols="9">
              <div class="d-flex align-center mb-2">
                <span class="mr-2 text-body-2 font-weight-medium"
                  >#{{ index + 1 }}</span
                >
                <v-text-field
                  :value="
                    typeof source === 'string' ? source : source.url || ''
                  "
                  outlined
                  dense
                  hide-details
                  class="flex-grow-1"
                  placeholder="https://example.com/article"
                  @input="updateSourceUrlAtIndex(index, $event)"
                ></v-text-field>
                <v-btn
                  icon
                  small
                  color="error"
                  class="ml-2"
                  :disabled="loadingSourceIndices.includes(index)"
                  @click="removeSourceField(index)"
                  v-if="sources.length > 1"
                >
                  <v-icon small>{{ icons.mdiClose }}</v-icon>
                </v-btn>
              </div>
              <v-textarea
                :value="typeof source === 'string' ? '' : source.content || ''"
                outlined
                dense
                rows="4"
                hide-details
                placeholder="Contenu de la source (sera rempli automatiquement après le scraping ou entrez manuellement)"
                class="ml-7"
                @input="updateSourceContentAtIndex(index, $event)"
              ></v-textarea>
              <v-alert
                v-if="
                  source && typeof source !== 'string' && source.scrapeError
                "
                type="error"
                dense
                text
                class="ml-7 mt-1 mb-0 py-1 text-caption"
              >
                <v-icon left small color="error">mdi-alert</v-icon>
                Échec du scraping :
                {{ scrapeLabel(source.scrapeStatus, source.scrapeError) }}
              </v-alert>
            </v-col>
            <v-col cols="3" class="d-flex align-center">
              <v-btn
                color="primary"
                block
                @click="handleScrapeSingleSource(index)"
                :loading="loadingSourceIndices.includes(index)"
                :disabled="
                  loadingSourceIndices.includes(index) ||
                  !canScrapeSource(index)
                "
              >
                <v-icon left small>{{ icons.mdiDownload }}</v-icon>
                Scraper
              </v-btn>
            </v-col>
          </v-row>

          <div v-if="sources.length >= 3" class="mt-2">
            <v-alert type="info" dense text>
              {{ sources.length }} sources ajoutées
            </v-alert>
          </div>
        </v-col>
      </v-row>

      <v-row>
        <v-col class="d-flex" cols="12">
          <v-btn
            color="primary"
            @click="handleScrapeSources"
            :loading="isScrapingLoading"
            :disabled="!canScrape"
          >
            Scraper les sources
          </v-btn>

          <v-switch
            v-model="allowAutoScrape"
            label="Scraping auto (non recommandé)"
            inset
            class="mt-1 ml-6"
          ></v-switch>

          <v-chip
            v-if="scrapedSources && scrapedSources.success_count"
            class="mt-1 ml-3"
            color="success"
            text-color="white"
          >
            <v-icon left>mdi-check</v-icon>
            {{ scrapedSources.success_count }} sources scrapées
          </v-chip>

          <v-chip
            v-if="scrapedSources && scrapedSources.error_count > 0"
            class="mt-1 ml-3"
            color="error"
            text-color="white"
          >
            <v-icon left>mdi-alert</v-icon>
            {{ scrapedSources.error_count }} erreurs
          </v-chip>

          <v-alert
            v-if="failedScrapes.length"
            type="error"
            dense
            text
            class="mt-2 mb-0"
          >
            <div class="font-weight-medium mb-1">
              {{ failedScrapes.length }} source(s) non scrapée(s) :
            </div>
            <ul class="pl-4 mb-0">
              <li
                v-for="fail in failedScrapes"
                :key="'scrape-fail-' + fail.index"
              >
                <span class="font-weight-medium">#{{ fail.index }}</span>
                — {{ fail.url }} :
                <em>{{ scrapeLabel(fail.status, fail.error) }}</em>
              </li>
            </ul>
          </v-alert>
        </v-col>
      </v-row>
      <v-dialog v-model="multiSourcesDialog" max-width="600px">
        <v-card>
          <v-card-title>
            <span class="text-h5">Ajouter plusieurs sources</span>
          </v-card-title>
          <v-card-text>
            <v-textarea
              v-model="multiSourcesText"
              label="Ajouter la liste des urls sources, une par ligne"
              outlined
              rows="10"
              autofocus
            ></v-textarea>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="blue darken-1"
              text
              @click="multiSourcesDialog = false"
            >
              Annuler
            </v-btn>
            <v-btn color="blue darken-1" text @click="validateMultiSources">
              Valider
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters, mapActions, mapState } from "vuex";
import { mdiPlusBox, mdiClose, mdiDownload } from "@mdi/js";
import store from "@/store";
import { NOTIF_DISPLAY_DURATION } from "@/utils/constants";
import axios from "@axios";

const STATUS_LABELS = {
  not_found: "Page introuvable (404)",
  empty: "Contenu vide / page non lisible",
  redirected: "Redirigé vers l'accueil",
  blocked: "Accès bloqué (anti-bot / site protégé)",
  consent: "Mur de cookies / consentement non accepté",
  paywall: "Contenu payant (abonnement requis)",
  forbidden: "Accès refusé / contenu protégé",
  http_error: "Erreur HTTP",
  unreachable: "Injoignable (timeout / connexion)",
  error: "Échec du scraping",
};

const fakeTopic =
  "Le temps de réaction incroyable des chats et leurs concurrents";
const fakeSourceUrl =
  "https://www.maxoe.com/zoom/divers-news/actu-animaux/qui-du-chat-ou-du-serpent-est-le-plus-rapide/";

const fakeSourceText = `# Qui du chat ou du serpent est le plus rapide ?
C'est une question que l'on est en droit de se poser lorsque l'on voit l'agilité de certains chats et leurs réflexes : le chat peut-il se mesurer à un serpent, est-il plus rapide que lui ?
Et bien sachez qu'un serpent a une vitesse d'attaque de 44 à 70 millisecondes. Un chat (pas trop patapouf et qui a l'habitude de chasser) a quant à lui une vitesse de réaction de 20 à 70 millisecondes !
Il se pourrait donc bien que le chat soit effectivement plus 'rapide' qu'un serpent…`;

export default {
  name: "TopicSourcesForm",

  props: {
    siteId: {
      type: [Number, String],
      default: null,
    },
  },

  data() {
    return {
      icons: {
        mdiPlus: mdiPlusBox,
        mdiClose: mdiClose,
        mdiDownload: mdiDownload,
      },
      isSourceAutoLoading: false,
      isScrapingLoading: false,
      loadingSourceIndices: [],
      topicError: false,
      multiSourcesDialog: false,
      multiSourcesText: "",
    };
  },

  computed: {
    ...mapGetters("aiFactoryTmp", [
      "getTopic",
      "getTopicIsTitle",
      "getSources",
      "getSelectedSite",
      "getScrapedSources",
      "getAllowAutoScrape",
      "getSkipImage",
      //"updateAllowAutoScrape",
      //"updateSkipImage",
    ]),
    ...mapState("aiFactoryTmp", ["allowAutoScrape", "skipImage"]),

    allowAutoScrape: {
      get() {
        return this.getAllowAutoScrape;
      },
      set(value) {
        this.updateAllowAutoScrape(value);
      },
    },

    skipImage: {
      get() {
        return this.getSkipImage;
      },
      set(value) {
        this.updateSkipImage(value);
      },
    },

    topic: {
      get() {
        return this.getTopic;
      },
      set(value) {
        this.updateTopic(value);
      },
    },

    topicIsTitle: {
      get() {
        return this.getTopicIsTitle;
      },
      set(value) {
        this.updateTopicIsTitle(value);
      },
    },

    topicCharacterCount() {
      return this.countTopicCharacters(this.topic);
    },

    topicCharacterLimit() {
      return this.topicIsTitle ? 250 : 255;
    },

    topicCharactersRemaining() {
      return this.topicCharacterLimit - this.topicCharacterCount;
    },

    topicTooLong() {
      return this.topicCharacterCount > this.topicCharacterLimit;
    },

    topicNearLimit() {
      return (
        !this.topicTooLong &&
        this.topicCharacterCount > 0 &&
        this.topicCharactersRemaining <= 20
      );
    },

    topicValidationMessages() {
      if (this.topicTooLong) {
        const label = this.topicIsTitle ? "Le titre définitif" : "Le topic";
        const excess = Math.abs(this.topicCharactersRemaining);
        return [
          `${label} dépasse la limite de ${this.topicCharacterLimit} caractères. ` +
            `Retire ${excess} caractère${excess > 1 ? "s" : ""}.`,
        ];
      }
      if (this.topicError && !this.topic.trim()) {
        return ["Renseigne un topic avant de continuer."];
      }
      return [];
    },

    topicLengthHint() {
      if (this.topicNearLimit) {
        return `${this.topicCharactersRemaining} caractère${
          this.topicCharactersRemaining > 1 ? "s" : ""
        } restant${this.topicCharactersRemaining > 1 ? "s" : ""}.`;
      }
      if (this.topicIsTitle) {
        return `Utilisé tel quel comme titre : 90 à 120 caractères recommandés, ${this.topicCharacterLimit} maximum.`;
      }
      return "Angle éditorial utilisé pour guider la génération : 255 caractères maximum.";
    },

    sources() {
      return this.getSources;
    },

    selectedSite: {
      get() {
        return this.getSelectedSite;
      },
      set(value) {
        this.updateSelectedSite(value);
      },
    },

    scrapedSources() {
      return this.getScrapedSources;
    },

    canScrape() {
      return (
        this.topic &&
        this.sources.filter((s) =>
          s && typeof s === "string" ? s.trim() : s?.url?.trim()
        ).length > 0
      );
    },

    canScrapeSource() {
      return (index) => {
        const source = this.sources[index];
        if (!this.topic) return false;
        const url = typeof source === "string" ? source : source?.url;
        return url && url.trim();
      };
    },

    failedScrapes() {
      const sources = this.scrapedSources?.sources || [];
      return sources.filter((s) => s && s.error);
    },
  },

  watch: {
    siteId: {
      immediate: true,
      handler(newSiteId) {
        if (newSiteId) {
          // Handle both direct site ID and object with site.id
          const siteIdToUse =
            typeof newSiteId === "object" && newSiteId.site
              ? newSiteId.site.id
              : newSiteId;
          if (siteIdToUse) {
            this.updateSelectedSite(siteIdToUse);
          }
        }
      },
    },
  },

  async mounted() {
    await this.initState();
    // Initialize with two empty sources if the store is empty
    const itemId = this.$route.params.id; // On récupère l'ID depuis l'URL

    if (itemId) {
      // Si un ID est présent, on est en mode "édition/reprise".
      // On appelle une nouvelle méthode pour charger les données.
      await this.loadDataForEdit(itemId);
    } else {
      // Sinon, on est en mode "création".
      // On exécute la logique originale : initialiser si le store est vide.
      if (this.getSources.length === 0) {
        this.updateSources(["", ""]);
      }
    }
    // if (!this.getTopic) {
    //   this.updateTopic(fakeTopic);
    // }
    // this.updateSources([
    //   {
    //     url: fakeSourceUrl,
    //     content: "",
    //     content: fakeSourceText,
    //   },
    // ]);
  },

  watch: {
    scrapedSources(newVal) {
      if (newVal && newVal.sources) {
        // Update sources with scraped content
        const updatedSources = [...this.sources];
        const sourceTargets = this.getScrapeTargets(updatedSources);
        const usedSourceIndices = new Set();

        newVal.sources.forEach((scrapedSource, responsePosition) => {
          const sourceIndex = this.getScrapedSourceTargetIndex(
            scrapedSource,
            sourceTargets,
            responsePosition,
            usedSourceIndices
          );

          if (sourceIndex === null) return;

          if (typeof scrapedSource.content === "string") {
            usedSourceIndices.add(sourceIndex);
            // Update the source with scraped content
            if (typeof updatedSources[sourceIndex] === "string") {
              updatedSources[sourceIndex] = {
                url: scrapedSource.url,
                content: scrapedSource.content || "",
                scrapeError: null,
                scrapeStatus: null,
              };
            } else {
              updatedSources[sourceIndex] = {
                ...updatedSources[sourceIndex],
                content: scrapedSource.content || "",
                scrapeError: null,
                scrapeStatus: null,
              };
            }
          } else if (scrapedSource.error) {
            usedSourceIndices.add(sourceIndex);
            const current = updatedSources[sourceIndex];
            const currentUrl =
              typeof current === "string"
                ? current
                : current?.url || scrapedSource.url;
            const currentContent =
              typeof current === "string" ? "" : current?.content || "";
            updatedSources[sourceIndex] = {
              url: currentUrl,
              content: currentContent,
              scrapeError: scrapedSource.error,
              scrapeStatus: scrapedSource.status || null,
            };
          }
        });
        this.updateSources(updatedSources);
      }
    },
  },

  methods: {
    ...mapActions("aiFactoryTmp", [
      "initState",
      "updateTopic",
      "updateTopicIsTitle",
      "updateSources",
      "addSource",
      "addSourceAuto",
      "removeSource",
      "updateSelectedSite",
      "scrapeSources",
      "scrapeSingleSource",
      "updateAllowAutoScrape",
      "updateImageHandler",
    ]),

    countTopicCharacters(value) {
      return Array.from(value || "").length;
    },

    getSourceUrl(source) {
      return typeof source === "string" ? source : source?.url || "";
    },

    normalizeUrlForMatch(url) {
      return String(url || "").trim();
    },

    getScrapeTargets(sources = this.sources) {
      return sources
        .map((source, index) => ({
          index,
          url: this.normalizeUrlForMatch(this.getSourceUrl(source)),
        }))
        .filter((target) => Boolean(target.url));
    },

    getScrapedSourceTargetIndex(
      scrapedSource,
      targets,
      responsePosition,
      usedSourceIndices
    ) {
      // Backend indexes are 1-based and relative to the request payload.
      const responseIndex = Number(scrapedSource.index);
      if (Number.isInteger(responseIndex) && responseIndex > 0) {
        const target = targets[responseIndex - 1];
        if (target && !usedSourceIndices.has(target.index)) {
          return target.index;
        }
      }

      const scrapedUrl = this.normalizeUrlForMatch(scrapedSource.url);
      const targetByUrl = targets.find(
        (target) =>
          !usedSourceIndices.has(target.index) &&
          this.normalizeUrlForMatch(target.url) === scrapedUrl
      );

      if (targetByUrl) {
        return targetByUrl.index;
      }

      const targetByPosition = targets[responsePosition];

      return targetByPosition && !usedSourceIndices.has(targetByPosition.index)
        ? targetByPosition.index
        : null;
    },

    scrapeLabel(status, error) {
      if (status && STATUS_LABELS[status]) return STATUS_LABELS[status];
      return this.humanizeScrapeError(error);
    },

    humanizeScrapeError(raw) {
      if (!raw) return "Échec inconnu";
      const s = String(raw);
      const e = s.toLowerCase();
      if (e.includes("redirect_home"))
        return "Redirigé vers l'accueil (article introuvable)";
      if (
        e.includes("consent_wall") ||
        e.includes("cookies") ||
        e.includes("consent")
      )
        return "Mur de cookies / consentement non accepté";
      if (
        e.includes("blocked") ||
        e.includes("bot") ||
        e.includes("captcha") ||
        e.includes("access denied")
      )
        return "Accès bloqué (détection anti-bot / site protégé)";
      if (
        e.includes("domain") ||
        e.includes("parked") ||
        e.includes("for sale") ||
        e.includes("à vendre")
      )
        return "Domaine parké / à vendre";
      if (
        e.includes("too_short") ||
        e.includes("empty") ||
        e.includes("returned empty content")
      )
        return "Contenu vide ou page non lisible";
      if (
        e.includes("http 404") ||
        e.includes("not found") ||
        e.includes("introuvable") ||
        e.includes("indisponible") ||
        e.includes("erreur 404") ||
        e.includes("404")
      )
        return "Page introuvable (404 / page d'erreur)";
      const httpMatch = s.match(/HTTP (\d{3})/i);
      if (httpMatch) {
        const code = httpMatch[1];
        if (code === "402") return "Contenu payant (abonnement requis)";
        if (code === "401" || code === "403")
          return "Accès refusé / contenu protégé";
        if (code === "404" || code === "410") return "Page introuvable (404)";
        if (code === "429") return "Trop de requêtes (rate-limit)";
        return `Erreur HTTP ${code}`;
      }
      if (e.includes("timeout")) return "Délai dépassé (timeout)";
      return s.length > 160 ? `${s.slice(0, 160)}…` : s;
    },

    getScrapeResponseSummary(response) {
      const sources = response?.sources || [];

      return {
        filledCount: sources.filter((source) => source?.content?.trim()).length,
        errorCount:
          response?.error_count ||
          sources.filter((source) => Boolean(source?.error)).length,
      };
    },

    async loadDataForEdit(id) {
      try {
        await store.dispatch("app/setSnackBar", {
          show: true,
          text: `Chargement des données pour la demande #${id}...`,
          color: "info",
          timeout: 2500,
        });

        const response = await axios.get(`/api/ai-factory/get-item`, {
          params: { id },
        });
        const itemData = response.data;

        if (itemData) {
          this.updateTopic(itemData.topic || "");
          if (
            Object.prototype.hasOwnProperty.call(itemData, "topic_is_title")
          ) {
            this.updateTopicIsTitle(!!itemData.topic_is_title);
          }
          this.updateSources(itemData.sources || []);
          this.updateSelectedSite(itemData.site_id || null);

          // si ton API renvoie allow_auto_scrape (bool)
          if (
            Object.prototype.hasOwnProperty.call(itemData, "allow_auto_scrape")
          ) {
            this.updateAllowAutoScrape(!!itemData.allow_auto_scrape);
          }

          // Mode image stocké ("generate" | "none" | "image_url" | "image_id").
          // Les valeurs legacy (noms de modèles, "default_config") sont
          // normalisées en "generate" côté back.
          if (Object.prototype.hasOwnProperty.call(itemData, "image_handler")) {
            this.updateImageHandler(itemData.image_handler || "generate");
          }

          await store.dispatch("app/setSnackBar", {
            show: true,
            text: `Données chargées avec succès.`,
            color: "success",
            timeout: NOTIF_DISPLAY_DURATION,
          });
        } else {
          throw new Error("Aucune donnée n'a été reçue pour cet identifiant.");
        }
      } catch (error) {
        console.error(
          "Erreur lors du chargement des données pour l'édition :",
          error
        );
        await store.dispatch("app/setSnackBar", {
          show: true,
          text: `Erreur: Impossible de charger les données. ${error.message}`,
          color: "error",
          timeout: NOTIF_DISPLAY_DURATION,
        });
      }
    },
    addSourceField() {
      this.addSource("");
    },

    validateMultiSources() {
      if (!this.multiSourcesText) {
        this.multiSourcesDialog = false;
        return;
      }
      const lines = this.multiSourcesText.split("\n");
      const newSources = [];
      const existingUrls = this.sources.map(s => (typeof s === 'string' ? s : s.url).trim().toLowerCase());
      const addedInThisBatch = new Set();

      lines.forEach((line) => {
        const trimmed = line.trim();
        const lowerTrimmed = trimmed.toLowerCase();
        if ((trimmed.startsWith("http://") || trimmed.startsWith("https://")) && 
            !existingUrls.includes(lowerTrimmed) && 
            !addedInThisBatch.has(lowerTrimmed)) {
          newSources.push({ url: trimmed, content: "" });
          addedInThisBatch.add(lowerTrimmed);
        }
      });

      if (newSources.length > 0) {
        const currentSources = [...this.sources];
        // Remove initial empty sources if any exist and we are adding real content
        const filteredCurrent = currentSources.filter(s => {
          const url = typeof s === 'string' ? s : s.url;
          return url && url.trim() !== "";
        });

        this.updateSources([...filteredCurrent, ...newSources]);

        store.dispatch("app/setSnackBar", {
          show: true,
          text: `${newSources.length} sources ajoutées (doublons ignorés)`,
          color: "success",
          timeout: 2000,
        });
      }

      this.multiSourcesText = "";
      this.multiSourcesDialog = false;
    },

    async addSourceAutoField() {
      if (!this.topic || this.topic.trim() === "") {
        await store.dispatch("app/setSnackBar", {
          show: true,
          text: `Remplir le champ "Topic" (angle) avant`,
          color: "error",
          timeout: 2000,
        });
        this.topicError = true;
        setTimeout(() => {
          this.topicError = false;
        }, 2000);
        return;
      }

      try {
        this.isSourceAutoLoading = true;
        // Pass the current site ID to the addSourceAuto action
        const urls = await this.addSourceAuto(this.siteId);
        await store.dispatch("app/setSnackBar", {
          show: true,
          text: `${urls.length} sources auto ajoutées`,
          color: "success",
          timeout: NOTIF_DISPLAY_DURATION,
        });

        console.log("Sources auto ajoutées:", urls);
        await store.dispatch("app/setSnackBar", {
          show: true,
          text: ` ajoutées`,
          color: "success",
          timeout: 2000,
        });
      } catch (error) {
        console.error(error);
        await store.dispatch("app/setSnackBar", {
          show: true,
          text: "Erreur lors de l'ajout de sources auto: " + error.message,
          color: "success",
          timeout: 2000,
        });
      }
      this.isSourceAutoLoading = false;
    },

    removeSourceField(index) {
      this.removeSource(index);
    },

    updateSourceUrlAtIndex(index, value) {
      const updatedSources = [...this.sources];
      if (typeof updatedSources[index] === "string") {
        updatedSources[index] = { url: value, content: "" };
      } else {
        updatedSources[index] = { ...updatedSources[index], url: value };
      }
      this.updateSources(updatedSources);
    },

    updateSourceContentAtIndex(index, value) {
      const updatedSources = [...this.sources];
      if (typeof updatedSources[index] === "string") {
        updatedSources[index] = { url: updatedSources[index], content: value };
      } else {
        updatedSources[index] = { ...updatedSources[index], content: value };
      }
      this.updateSources(updatedSources);
    },

    async handleScrapeSources() {
      this.isScrapingLoading = true;
      try {
        const scrapingPromises = this.sources
          .map((source, index) => {
            const url = typeof source === "string" ? source : source?.url;
            if (url && url.trim()) {
              return this.handleScrapeSingleSource(index, { silent: true });
            }
            return null;
          })
          .filter((promise) => promise !== null);

        const results = await Promise.all(scrapingPromises);
        const filledCount = results.filter(
          (result) => this.getScrapeResponseSummary(result).filledCount > 0
        ).length;
        const errorCount = results.filter(
          (result) => this.getScrapeResponseSummary(result).errorCount > 0
        ).length;

        if (filledCount > 0) {
          this.$toast?.success(`${filledCount} source(s) scrapée(s)`) ||
            console.log(`${filledCount} source(s) scraped`);
        } else if (errorCount === 0) {
          this.$toast?.warning(
            "Scraping terminé, mais aucun contenu n'a été récupéré."
          ) || console.warn("Scraping finished with empty content");
        }

        if (errorCount > 0) {
          this.$toast?.warning(`${errorCount} erreur(s) pendant le scraping`) ||
            console.warn(`${errorCount} scraping error(s)`);
        }
      } catch (error) {
        this.$toast?.error("Error scraping sources: " + error.message) ||
          console.error(error);
      } finally {
        this.isScrapingLoading = false;
      }
    },

    async handleScrapeSingleSource(index, { silent = false } = {}) {
      this.loadingSourceIndices.push(index);
      try {
        const source = this.sources[index];
        const url = typeof source === "string" ? source : source.url;

        const result = await this.scrapeSingleSource({ index, url });
        const summary = this.getScrapeResponseSummary(result);

        if (!silent) {
          if (summary.errorCount > 0) {
            this.$toast?.warning("Le scraping de cette source a échoué.") ||
              console.warn("Source scraping failed");
          } else if (summary.filledCount > 0) {
            this.$toast?.success("Source scrapée avec succès.") ||
              console.log("Source scraped");
          } else {
            this.$toast?.warning(
              "Scraping terminé, mais aucun contenu n'a été récupéré."
            ) || console.warn("Source scraping returned empty content");
          }
        }

        return result;
      } catch (error) {
        this.$toast?.error("Error scraping source: " + error.message) ||
          console.error(error);
        throw error;
      } finally {
        const loadingIndex = this.loadingSourceIndices.indexOf(index);
        if (loadingIndex > -1) {
          this.loadingSourceIndices.splice(loadingIndex, 1);
        }
      }
    },
  },
};
</script>
<style>
.topic-is-title-checkbox {
  position: relative;
  top: -4px;
}

.topic-field--near-limit .v-counter {
  color: #fb8c00 !important;
  font-weight: 600;
}

.topic-field--over-limit .v-counter {
  color: #ff5252 !important;
  font-weight: 600;
}
</style>
